import config from '@constants/config';
import { constants } from '@constants/index';
import { getUserEncrypted, setCache } from '@shared/utils';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

const { ENABLE_COOKIES } = config;

export interface AuthUser {
  mail?: string;
  correspondent?: string;
  correspondent_type_name?: string;
  role_name?: string;
  profile_id?: string;
  [key: string]: unknown;
}

interface AuthSliceState {
  user: AuthUser;
  isSignin?: boolean;
  loading?: boolean;
  errorMessage?: string | null;
}

interface UseAuthUser {
  correspondent?: string;
  force_chg_pass?: boolean;
  tenants?: string[];
  [key: string]: any;
}

export interface UseAuthReturn {
  isAuthenticated: boolean;
  user: UseAuthUser | null;
  hasMultipleTenants: boolean;
  forceChangePassword: boolean;
  isLoading: boolean;
}

export interface UseAuthCheckReturn {
  IS_SING_IN: boolean;
}

export function useCurrentUser(): AuthUser {
  const { user } = useSelector((state: { auth: AuthSliceState }) => state.auth);
  return user ?? {};
}

export function useIsSignin(): boolean {
  return useSelector((state: { auth: AuthSliceState }) => state.auth?.isSignin ?? false);
}

export function useAuthLoading(): boolean {
  return useSelector((state: { auth: AuthSliceState }) => state.auth?.loading ?? false);
}

export function useAuthErrorMessage(): string | null {
  return useSelector((state: { auth: AuthSliceState }) => state.auth?.errorMessage ?? null);
}

const getStorageToken = (): string | null => {
  try {
    return localStorage.getItem('TOKEN');
  } catch {
    return null;
  }
};

export const useAuthCheck = (): UseAuthCheckReturn => {
  const IS_SING_IN = useIsSignin();

  useEffect(() => {
    if (IS_SING_IN) {
      setCache(constants.redirectKey, '');
    } else {
      const [, path] = location.href.split('#');
      if (path && !path.includes('auth/') && !path.includes('tenants')) {
        setCache(constants.redirectKey, `#${path}`);
      }
    }
  }, [IS_SING_IN]);

  useEffect(() => {
    const [, path] = location.href.split('#');
    if (path && !path.includes('auth/') && !path.includes('tenants')) {
      setCache(constants.redirectKey, `#${path}`);
    }
  }, []);

  return {
    IS_SING_IN,
  };
};

export const useAuth = (): UseAuthReturn => {
  const { IS_SING_IN } = useAuthCheck();
  const loading = useAuthLoading();

  const authState = useMemo(() => {
    const token = getStorageToken();
    const isAuthenticated = ENABLE_COOKIES ? !!IS_SING_IN : !!(IS_SING_IN || token);

    let user: UseAuthUser | null = null;
    let hasMultipleTenants = false;
    let forceChangePassword = false;

    try {
      user = getUserEncrypted();
      hasMultipleTenants = (user?.tenants?.length ?? 0) > 1;
      forceChangePassword = user?.force_chg_pass ?? false;
    } catch {
      user = null;
    }

    return {
      isAuthenticated,
      user,
      hasMultipleTenants,
      forceChangePassword,
      isLoading: loading,
    };
  }, [IS_SING_IN, loading]);

  return authState;
};
